import React from 'react';
import NumberFormat from 'react-number-format';

import { Typography } from '@mui/material';
import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { PHONE_NUMBER_FORMAT } from 'constants/ui';
import { getCleanPhoneNumber } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import ActionButton from './ActionButton';
import { TableDataType } from './AgentAndSeller.constants';

const agentAndSellerFields = {
  agentName: {
    fieldId: 'agentName',
    type: FieldType.INPUT,
    nameInModel: 'name',
    inputProps: {
      placeholder: 'Agent Name',
      label: 'Agent Name',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  agentNmlsId: {
    fieldId: 'agentNmlsId',
    type: FieldType.INPUT,
    nameInModel: 'agentNmlsId',
    inputProps: {
      placeholder: 'Agent License Number',
      label: 'Agent License Number',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  agentStateLicenseId: {
    fieldId: 'agentStateLicenseId',
    type: FieldType.INPUT,
    nameInModel: 'agentStateLicenseId',
    inputProps: {
      maxLength: 30,
      placeholder: 'Agent State License ID',
      label: 'Agent State License ID',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  agentOffice: {
    fieldId: 'office',
    type: FieldType.INPUT,
    nameInModel: 'office',
    inputProps: {
      placeholder: 'Agent Office',
      label: 'Agent Office',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  companyNmlsId: {
    fieldId: 'companyNmlsId',
    type: FieldType.INPUT,
    nameInModel: 'companyNmlsId',
    inputProps: {
      placeholder: 'Company License Number',
      label: 'Company License Number',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  companyStateLicenseId: {
    fieldId: 'companyStateLicenseId',
    type: FieldType.INPUT,
    nameInModel: 'companyStateLicenseId',
    inputProps: {
      maxLength: 30,
      placeholder: 'Company State License ID',
      label: 'Company State License ID',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: 'email',
    inputProps: {
      placeholder: 'Email',
      label: 'Email',
      required: false,
      variant: 'outlined',
      type: FieldInputType.EMAIL,
    },
  },
  phone: {
    fieldId: 'phone',
    type: FieldType.TEL,
    nameInModel: 'phone',
    inputProps: {
      placeholder: 'Phone',
      label: 'Phone',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  fax: {
    fieldId: 'fax',
    type: FieldType.TEL,
    nameInModel: 'fax',
    inputProps: {
      placeholder: 'Fax',
      label: 'Fax',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: 'firstName',
    inputProps: {
      placeholder: 'First Name',
      label: 'First Name',
      required: false,
      variant: 'outlined',
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: 'middleName',
    inputProps: {
      placeholder: 'Middle Name',
      label: 'Middle Name',
      required: false,
      variant: 'outlined',
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: 'lastName',
    inputProps: {
      placeholder: 'Last Name',
      label: 'Last Name',
      required: false,
      variant: 'outlined',
    },
  },
  companyName: {
    fieldId: 'companyName',
    type: FieldType.INPUT,
    nameInModel: 'companyName',
    inputProps: {
      label: 'Company Name',
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
};

export const getAgentConfig = compose(fillConfigFieldsWithKeys, () => {
  return {
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          agentAndSellerFields.agentName,
          agentAndSellerFields.agentNmlsId,
          agentAndSellerFields.agentStateLicenseId,
          agentAndSellerFields.agentOffice,
          agentAndSellerFields.companyNmlsId,
          agentAndSellerFields.companyStateLicenseId,
          agentAndSellerFields.email,
          agentAndSellerFields.phone,
          agentAndSellerFields.fax,
        ],
        key: `selling-agent`,
      },
      {
        type: FieldType.GROUP,
        fields: Object.values(
          getAddressFieldsConfig({
            prefix: '',
            isWithCounty: false,
            isWithCountry: false,
            isAllFieldsNonRequired: true,
          }),
        ),
        key: `selling-agent-address`,
        lockAsGroup: true,
      },
    ],
  };
});

export const getSellerConfig = compose(fillConfigFieldsWithKeys, () => {
  return {
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          agentAndSellerFields.firstName,
          agentAndSellerFields.middleName,
          agentAndSellerFields.lastName,
          agentAndSellerFields.email,
          agentAndSellerFields.phone,
          agentAndSellerFields.companyName,
        ],
        key: `listing-agent`,
      },
      {
        type: FieldType.GROUP,
        fields: Object.values(
          getAddressFieldsConfig({
            prefix: '',
            isWithCounty: false,
            isWithCountry: false,
            isAllFieldsNonRequired: true,
          }),
        ),
        key: `listing-agent-address`,
        lockAsGroup: true,
      },
    ],
  };
});

export const getAgentInfoTableConfig = ({
  setEditableTableData,
  deleteTableData,
  editableRowId,
  isActionDisabled,
  entityType,
}) => ({
  name: {
    label: 'Agent Name',
    width: '27%',
    sort: true,
    colSpan: ({ name }) => name?.colSpan,
    customStyles: ({ name }) => name?.customStyles,
    content: ({ name }) =>
      name?.content ?? (
        <Typography data-truncated className="truncate" variant="regular14">
          {name}
        </Typography>
      ),
  },
  agentStateLicenseNumber: {
    label: 'Agent License Number',
    sort: true,
    width: '16%',
    content: ({ agentNmlsId }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {agentNmlsId}
      </Typography>
    ),
  },
  agentStateLicenseId: {
    label: 'Agent State License ID',
    sort: true,
    width: '16%',
    content: ({ agentStateLicenseId }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {agentStateLicenseId}
      </Typography>
    ),
  },
  email: {
    label: 'Email',
    sort: true,
    width: '16%',
    content: ({ email }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {email}
      </Typography>
    ),
  },
  phone: {
    label: 'Phone',
    sort: true,
    width: '16%',
    content: ({ phone }) => (
      <NumberFormat
        data-truncated
        className="truncate"
        displayType="text"
        format={PHONE_NUMBER_FORMAT}
        value={phone && getCleanPhoneNumber(phone)}
      />
    ),
  },
  action: {
    label: 'Action',
    width: '9%',
    content: rowData => (
      <ActionButton
        deleteTableData={deleteTableData}
        entityType={entityType}
        isDisabled={isActionDisabled || (!!editableRowId && rowData.id !== editableRowId)}
        isEditDisabled={rowData.id === editableRowId}
        rowData={rowData}
        setEditableTableData={setEditableTableData}
      />
    ),
    align: 'center',
  },
});

export const getSellerInfoTableConfig = ({
  setEditableTableData,
  deleteTableData,
  editableRowId,
  isActionDisabled,
}) => ({
  sellerName: {
    label: 'Seller Name',
    width: '27%',
    sort: true,
    colSpan: ({ sellerName }) => sellerName?.colSpan,
    customStyles: ({ sellerName }) => sellerName?.customStyles,
    content: ({ sellerName }) =>
      sellerName?.content ?? (
        <Typography data-truncated className="truncate" variant="regular14">
          {sellerName}
        </Typography>
      ),
  },
  email: {
    label: 'Email',
    sort: true,
    width: '16%',
    content: ({ email }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {email}
      </Typography>
    ),
  },
  phone: {
    label: 'Phone',
    sort: true,
    width: '16%',
    content: ({ phone }) => (
      <NumberFormat
        data-truncated
        className="truncate"
        displayType="text"
        format={PHONE_NUMBER_FORMAT}
        value={phone && getCleanPhoneNumber(phone)}
      />
    ),
  },
  fullAddress: {
    label: 'Address',
    width: '16%',
    sort: true,
    fieldName: ['city', 'country', 'county', 'state', 'street', 'unit', 'zip'],
    content: ({ fullAddress }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {fullAddress}
      </Typography>
    ),
  },
  companyName: {
    label: 'Company',
    sort: true,
    width: '16%',
    content: ({ companyName }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {companyName}
      </Typography>
    ),
  },
  action: {
    label: 'Action',
    width: '9%',
    content: rowData => (
      <ActionButton
        deleteTableData={deleteTableData}
        entityType={TableDataType.SELLER}
        isDisabled={isActionDisabled || (!!editableRowId && rowData.id !== editableRowId)}
        isEditDisabled={rowData.id === editableRowId}
        rowData={rowData}
        setEditableTableData={setEditableTableData}
      />
    ),
    align: 'center',
  },
});
